// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xScreen } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import React from "react";
import { css } from "styled-components";

const enum fields {
    paddingSize = "paddingSize",
    ipadPaddingSize = "ipadPaddingSize",
    mobilePaddingSize = "mobilePaddingSize",
}

interface SpacerProps extends WidgetProps {
    fields: {
        [fields.paddingSize]: string;
        [fields.ipadPaddingSize]: string;
        [fields.mobilePaddingSize]: string;
    };
}

const spacerTemplateWidget: React.FC<SpacerProps> = props => {
    const paddingSize = Number(props.fields.paddingSize);
    const ipadPaddingSize = Number(props.fields.ipadPaddingSize);
    const mobilePaddingSize = Number(props.fields.mobilePaddingSize);

    const cssBlock = css`
        height: ${paddingSize}px;
        display: flex;
        flex-direction: column;
        justify-content: center;

        ${xScreen("lte", "md")} {
            height: ${ipadPaddingSize}px;
        }

        ${xScreen("lte", "sm")} {
            height: ${mobilePaddingSize}px;
        }
    `;

    return <StyledWrapper css={cssBlock} />;
};

export const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    fieldDefinitions: [
        {
            name: fields.paddingSize,
            displayName: "Desktop Padding Size",
            editorTemplate: "DropDownField",
            options: [
                {
                    displayName: "0px",
                    value: "0",
                },
                {
                    displayName: "5px",
                    value: "5",
                },
                {
                    displayName: "10px",
                    value: "10",
                },
                {
                    displayName: "15px",
                    value: "15",
                },
                {
                    displayName: "20px",
                    value: "20",
                },
                {
                    displayName: "25px",
                    value: "25",
                },
                {
                    displayName: "30px",
                    value: "30",
                },
                {
                    displayName: "40px",
                    value: "40",
                },
                {
                    displayName: "50px",
                    value: "50",
                },
                {
                    displayName: "60px",
                    value: "60",
                },
                {
                    displayName: "75px",
                    value: "75",
                },
                {
                    displayName: "100px",
                    value: "100",
                },
            ],
            isRequired: true,
            defaultValue: "50",
            fieldType: "General",
        },
        {
            name: fields.ipadPaddingSize,
            displayName: "iPad Padding Size",
            editorTemplate: "DropDownField",
            options: [
                {
                    displayName: "0px",
                    value: "0",
                },
                {
                    displayName: "5px",
                    value: "5",
                },
                {
                    displayName: "10px",
                    value: "10",
                },
                {
                    displayName: "15px",
                    value: "15",
                },
                {
                    displayName: "20px",
                    value: "20",
                },
                {
                    displayName: "25px",
                    value: "25",
                },
                {
                    displayName: "30px",
                    value: "30",
                },
                {
                    displayName: "40px",
                    value: "40",
                },
                {
                    displayName: "50px",
                    value: "50",
                },
                {
                    displayName: "60px",
                    value: "60",
                },
                {
                    displayName: "75px",
                    value: "75",
                },
                {
                    displayName: "100px",
                    value: "100",
                },
            ],
            isRequired: true,
            defaultValue: "50",
            fieldType: "General",
        },
        {
            name: fields.mobilePaddingSize,
            displayName: "Mobile Padding Size",
            editorTemplate: "DropDownField",
            options: [
                {
                    displayName: "0px",
                    value: "0",
                },
                {
                    displayName: "5px",
                    value: "5",
                },
                {
                    displayName: "10px",
                    value: "10",
                },
                {
                    displayName: "15px",
                    value: "15",
                },
                {
                    displayName: "20px",
                    value: "20",
                },
                {
                    displayName: "25px",
                    value: "25",
                },
                {
                    displayName: "30px",
                    value: "30",
                },
                {
                    displayName: "40px",
                    value: "40",
                },
                {
                    displayName: "50px",
                    value: "50",
                },
                {
                    displayName: "60px",
                    value: "60",
                },
                {
                    displayName: "75px",
                    value: "75",
                },
                {
                    displayName: "100px",
                    value: "100",
                },
            ],
            isRequired: true,
            defaultValue: "50",
            fieldType: "General",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: spacerTemplateWidget,
    definition,
};

export default widgetModule;
